/**
 * EXIF orientation — read and rewrite a JPEG's orientation marker.
 *
 * This is how rotation survives export without touching a single pixel: we copy
 * the file byte-for-byte and flip the one marker that tells viewers which way up
 * the image goes. No decode, no re-encode, no generational quality loss.
 *
 * Pure module: no imports, no I/O.
 */

export type Orientation = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/** "Exif\0\0" — the APP1 payload signature. */
const EXIF_SIG = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00] as const;
const TAG_ORIENTATION = 0x0112;
const TYPE_SHORT = 3;

export interface RotationResult {
  bytes: Uint8Array;
  /** False when the file carries EXIF that has no orientation tag to patch. */
  applied: boolean;
  orientation: Orientation;
}

/* ------------------------------------------------------------------ *
 * Orientation algebra
 * ------------------------------------------------------------------ */

/** Decompose an orientation into the clockwise rotation and mirror it encodes. */
export function decomposeOrientation(o: Orientation): {
  degrees: number;
  mirrored: boolean;
} {
  switch (o) {
    case 1:
      return { degrees: 0, mirrored: false };
    case 2:
      return { degrees: 0, mirrored: true };
    case 3:
      return { degrees: 180, mirrored: false };
    case 4:
      return { degrees: 180, mirrored: true };
    case 5:
      return { degrees: 90, mirrored: true };
    case 6:
      return { degrees: 90, mirrored: false };
    case 7:
      return { degrees: 270, mirrored: true };
    case 8:
      return { degrees: 270, mirrored: false };
  }
}

/** Rebuild an orientation value from a clockwise rotation plus mirror flag. */
export function composeOrientation(degrees: number, mirrored: boolean): Orientation {
  const d = normaliseDegrees(degrees);
  if (!mirrored) {
    if (d === 0) return 1;
    if (d === 90) return 6;
    if (d === 180) return 3;
    return 8;
  }
  if (d === 0) return 2;
  if (d === 90) return 5;
  if (d === 180) return 4;
  return 7;
}

/**
 * Add a clockwise rotation on top of an existing orientation.
 *
 * For a mirrored image the extra rotation composes in the opposite direction,
 * because a reflection conjugates rotation: R(d)∘M = M∘R(-d).
 */
export function addRotation(existing: Orientation, degrees: number): Orientation {
  const { degrees: base, mirrored } = decomposeOrientation(existing);
  const next = mirrored ? base - degrees : base + degrees;
  return composeOrientation(next, mirrored);
}

/** Clamp any integer degree value to one of 0 / 90 / 180 / 270. */
export function normaliseDegrees(degrees: number): 0 | 90 | 180 | 270 {
  const d = ((Math.round(degrees / 90) * 90) % 360 + 360) % 360;
  return d as 0 | 90 | 180 | 270;
}

/* ------------------------------------------------------------------ *
 * JPEG structure
 * ------------------------------------------------------------------ */

export function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

interface App1 {
  /** Index of the segment's 0xFF marker byte. */
  segStart: number;
  /** First byte after the 2-byte length field. */
  payloadStart: number;
  payloadEnd: number;
}

function findExifApp1(bytes: Uint8Array): App1 | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;

  let p = 2;
  while (p + 4 <= bytes.length) {
    if (bytes[p] !== 0xff) return null;

    const marker = bytes[p + 1];

    // Standalone markers carry no length payload.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      p += 2;
      continue;
    }
    // Start of scan — compressed data follows, no more metadata segments.
    if (marker === 0xda) return null;

    const len = (bytes[p + 2] << 8) | bytes[p + 3];
    if (len < 2) return null;

    const payloadStart = p + 4;
    const payloadEnd = p + 2 + len;
    if (payloadEnd > bytes.length) return null;

    if (marker === 0xe1 && payloadEnd - payloadStart >= 6) {
      let matches = true;
      for (let i = 0; i < 6; i++) {
        if (bytes[payloadStart + i] !== EXIF_SIG[i]) {
          matches = false;
          break;
        }
      }
      if (matches) return { segStart: p, payloadStart, payloadEnd };
    }

    p = payloadEnd;
  }
  return null;
}

interface Tiff {
  little: boolean;
  ifd0Offset: number;
  tiffStart: number;
}

function readTiffHeader(bytes: Uint8Array, tiffStart: number): Tiff | null {
  if (tiffStart + 8 > bytes.length) return null;

  const b0 = bytes[tiffStart];
  const b1 = bytes[tiffStart + 1];
  let little: boolean;
  if (b0 === 0x49 && b1 === 0x49) little = true;
  else if (b0 === 0x4d && b1 === 0x4d) little = false;
  else return null;

  const dv = view(bytes);
  if (dv.getUint16(tiffStart + 2, little) !== 42) return null;

  const ifd0Offset = dv.getUint32(tiffStart + 4, little);
  if (tiffStart + ifd0Offset + 2 > bytes.length) return null;

  return { little, ifd0Offset, tiffStart };
}

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

/** Absolute byte offset of the IFD0 entry carrying `tag`, or null. */
function findIfd0Entry(bytes: Uint8Array, tiff: Tiff, tag: number): number | null {
  const dv = view(bytes);
  const base = tiff.tiffStart + tiff.ifd0Offset;
  const count = dv.getUint16(base, tiff.little);

  for (let i = 0; i < count; i++) {
    const entry = base + 2 + i * 12;
    if (entry + 12 > bytes.length) return null;
    if (dv.getUint16(entry, tiff.little) === tag) return entry;
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

/** Read a JPEG's stored orientation. Defaults to 1 when absent or unreadable. */
export function readOrientation(bytes: Uint8Array): Orientation {
  const app1 = findExifApp1(bytes);
  if (!app1) return 1;

  const tiff = readTiffHeader(bytes, app1.payloadStart + 6);
  if (!tiff) return 1;

  const entry = findIfd0Entry(bytes, tiff, TAG_ORIENTATION);
  if (entry === null) return 1;

  const value = view(bytes).getUint16(entry + 8, tiff.little);
  return value >= 1 && value <= 8 ? (value as Orientation) : 1;
}

/**
 * Return a copy of `bytes` whose orientation marker reads `orientation`.
 *
 * Three cases:
 *   A. an orientation tag exists  → patched in place, same file length
 *   B. no EXIF segment at all     → a minimal EXIF APP1 is inserted after SOI
 *   C. EXIF present, tag missing  → left alone; `applied` comes back false
 *
 * Case C is rare (camera JPEGs always carry the tag). The caller is expected to
 * surface it rather than silently ship an unrotated copy.
 */
export function setOrientation(bytes: Uint8Array, orientation: Orientation): RotationResult {
  // Never splice a segment into something that is not a JPEG — that would
  // corrupt the copy rather than rotate it.
  if (!isJpeg(bytes)) {
    return { bytes: new Uint8Array(bytes), applied: false, orientation: 1 };
  }

  const app1 = findExifApp1(bytes);

  if (!app1) {
    return { bytes: insertMinimalExif(bytes, orientation), applied: true, orientation };
  }

  const tiff = readTiffHeader(bytes, app1.payloadStart + 6);
  if (!tiff) {
    return { bytes: new Uint8Array(bytes), applied: false, orientation: 1 };
  }

  const entry = findIfd0Entry(bytes, tiff, TAG_ORIENTATION);
  if (entry === null) {
    return { bytes: new Uint8Array(bytes), applied: false, orientation: readOrientation(bytes) };
  }

  const out = new Uint8Array(bytes);
  const dv = view(out);
  dv.setUint16(entry + 2, TYPE_SHORT, tiff.little);
  dv.setUint32(entry + 4, 1, tiff.little);
  dv.setUint16(entry + 8, orientation, tiff.little);
  dv.setUint16(entry + 10, 0, tiff.little);

  return { bytes: out, applied: true, orientation };
}

/**
 * Bake an extra clockwise rotation into a JPEG's orientation marker.
 * A rotation of 0 is a straight copy — no parsing, no risk.
 */
export function rotateJpeg(bytes: Uint8Array, degrees: number): RotationResult {
  const delta = normaliseDegrees(degrees);
  const current = readOrientation(bytes);

  if (delta === 0) {
    return { bytes: new Uint8Array(bytes), applied: isJpeg(bytes), orientation: current };
  }
  return setOrientation(bytes, addRotation(current, delta));
}

/** Build a 36-byte EXIF APP1 holding nothing but orientation, and splice it in after SOI. */
function insertMinimalExif(bytes: Uint8Array, orientation: Orientation): Uint8Array {
  const PAYLOAD = 32; // 6 sig + 8 TIFF header + 2 count + 12 entry + 4 next-IFD
  const segment = new Uint8Array(4 + PAYLOAD);
  const dv = new DataView(segment.buffer);

  segment[0] = 0xff;
  segment[1] = 0xe1;
  dv.setUint16(2, PAYLOAD + 2, false); // length includes itself

  segment.set(EXIF_SIG, 4);

  const t = 4 + 6; // TIFF header start within the segment
  segment[t] = 0x4d; // big-endian, so every field below writes with little=false
  segment[t + 1] = 0x4d;
  dv.setUint16(t + 2, 42, false);
  dv.setUint32(t + 4, 8, false); // IFD0 sits 8 bytes into the TIFF block

  const ifd0 = t + 8;
  dv.setUint16(ifd0, 1, false); // one entry
  dv.setUint16(ifd0 + 2, TAG_ORIENTATION, false);
  dv.setUint16(ifd0 + 4, TYPE_SHORT, false);
  dv.setUint32(ifd0 + 6, 1, false);
  dv.setUint16(ifd0 + 10, orientation, false);
  dv.setUint16(ifd0 + 12, 0, false);
  dv.setUint32(ifd0 + 14, 0, false); // no IFD1

  const out = new Uint8Array(bytes.length + segment.length);
  out.set(bytes.subarray(0, 2), 0); // SOI
  out.set(segment, 2);
  out.set(bytes.subarray(2), 2 + segment.length);
  return out;
}
